import React, { useEffect, useRef, useState } from "react";
import "../../pages/signin/sigin.css";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import {
  fetchProfileDashboard,
  hasRequiredAddressInfo,
  hasRequiredPersonalInfo,
} from "../../api/profile.api";

const PROFILE_COMPLETION_PROMPT_KEY = "show_profile_completion_prompt";

const SignUp = ({ onClose, onSwitchToSignIn }) => {
  const [mode, setMode] = useState("email");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    email: "",
    password: "",
    confirmPassword: "",
    referral_code: "",
    agreeTerms: false
  });
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [phoneStep, setPhoneStep] = useState("phone");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const recaptchaRef = useRef(null);

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      phone_number: "",
      email: "",
      password: "",
      confirmPassword: "",
      referral_code: "",
      agreeTerms: false,
    });
    resetPhoneState();
    setError("");
    setSuccessMessage("");
    setInfoMessage("");
    setIsLoading(false);
  };

  const { signUp, signInWithPhone } = useAuth();

  useEffect(() => {
    return () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setTimeout(() => {
      setResendCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const resetPhoneState = () => {
    setPhoneStep("phone");
    setPhoneNumber("");
    setOtpCode("");
    setConfirmationResult(null);
    setResendCooldown(0);
    if (recaptchaRef.current) {
      recaptchaRef.current.clear();
      recaptchaRef.current = null;
    }
  };

  const normalizePhoneNumber = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return "";

    if (trimmed.startsWith("+")) {
      return trimmed;
    }

    const digits = trimmed.replace(/\D/g, "");
    if (digits.length === 10) {
      return `+91${digits}`;
    }

    if (digits.length > 10) {
      return `+${digits}`;
    }

    return "";
  };

  const getRecaptchaVerifier = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.clear();
    }

    recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container-signup", {
      size: "invisible",
    });

    return recaptchaRef.current;
  };

  const getFirebaseAuthErrorMessage = (err, phase = "send") => {
    const code = err?.code || "";

    if (code === "auth/configuration-not-found" || code === "auth/operation-not-allowed") {
      return "Phone login is disabled in Firebase. Enable Phone provider in Firebase Console > Authentication > Sign-in method.";
    }

    if (code === "auth/unauthorized-domain") {
      return "This domain is not authorized for Firebase Auth. Add localhost and 127.0.0.1 in Firebase Console > Authentication > Settings > Authorized domains.";
    }

    if (code === "auth/invalid-phone-number") {
      return "Invalid phone number format. Use format like +919876543210.";
    }

    if (code === "auth/too-many-requests") {
      return "Too many OTP attempts. Please wait a few minutes and try again.";
    }

    if (code === "auth/captcha-check-failed" || code === "auth/missing-app-credential") {
      return "reCAPTCHA verification failed. Reload the page and try again.";
    }

    if (phase === "verify") {
      if (code === "auth/invalid-verification-code") {
        return "Invalid OTP code. Please check and try again.";
      }

      if (code === "auth/code-expired") {
        return "OTP expired. Please request a new OTP.";
      }
    }

    return err?.message || "Something went wrong with phone authentication. Please try again.";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading) return;

    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    if (
      !formData.first_name ||
      !formData.phone_number ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      setError("Please fill in all required fields");
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      setIsLoading(false);
      return;
    }

    const phoneDigits = formData.phone_number.replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      setError("Please enter a valid phone number");
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    if (!formData.agreeTerms) {
      setError("Please agree to terms and conditions");
      setIsLoading(false);
      return;
    }

    try {
      const result = await signUp({
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone_number: formData.phone_number,
        email: formData.email,
        password: formData.password,
        referral_code: formData.referral_code,
      });

      if (!result.success) {
        setError(result.message || "Signup failed");
        return;
      }

      setSuccessMessage("Account created successfully");

      setTimeout(() => {
        resetForm();
        onSwitchToSignIn();
        setSuccessMessage("");
      }, 1500);

    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (isLoading) return;
    if (phoneStep === "otp" && resendCooldown > 0) return;

    const formattedPhone = normalizePhoneNumber(phoneNumber);
    if (!formattedPhone) {
      setError("Enter a valid phone number (example: +919876543210).");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccessMessage("");
    setInfoMessage("");

    try {
      const verifier = getRecaptchaVerifier();
      const result = await signInWithPhoneNumber(auth, formattedPhone, verifier);
      setConfirmationResult(result);
      setPhoneStep("otp");
      setResendCooldown(30);
      setInfoMessage("OTP sent successfully. Please enter the OTP to continue.");
    } catch (err) {
      console.error("Signup phone OTP send failed:", err);
      setError(getFirebaseAuthErrorMessage(err, "send"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (isLoading) return;

    if (!confirmationResult) {
      setError("Please request OTP first.");
      return;
    }

    if (!otpCode.trim()) {
      setError("Please enter OTP.");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccessMessage("");
    setInfoMessage("");

    try {
      const credential = await confirmationResult.confirm(otpCode.trim());
      const idToken = await credential.user.getIdToken();
      const result = await signInWithPhone(idToken);

      if (result.success) {
        const profile = await fetchProfileDashboard().catch(() => null);
        const missingPersonal = !hasRequiredPersonalInfo(profile?.personal_info);
        const missingAddress = !hasRequiredAddressInfo(profile?.default_address);

        if (missingPersonal || missingAddress) {
          window.sessionStorage.setItem(PROFILE_COMPLETION_PROMPT_KEY, "1");
          onClose();
          window.location.reload();
          return;
        }

        onClose();
        window.location.reload();
      } else {
        setError(result.message || "Phone authentication failed.");
      }
    } catch (err) {
      console.error("Signup phone OTP verify failed:", err);
      setError(getFirebaseAuthErrorMessage(err, "verify"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMobileModeClick = () => {
    setError("");
    setSuccessMessage("");

    if (mode === "email") {
      setInfoMessage(
        "Sign up with mobile is currently not available. Please use email signup for now."
      );
      return;
    }

    setInfoMessage("");
    resetPhoneState();
    setMode("email");
  };

  return (
    <div className="cart-overlay signin-overlay" onClick={onClose}>
      <div className="cart-popup signin-popup" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cart-header">
          <h3>{mode === "email" ? "SIGN UP" : "PHONE SIGN UP"}</h3>
          <span className="close-btn" onClick={onClose}>×</span>
        </div>

        <div style={{ padding: "20px" }}>
          {error && (
            <div style={{
              padding: '10px',
              marginBottom: '15px',
              backgroundColor: '#fee',
              color: '#c33',
              borderRadius: '4px',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}
          {successMessage && (
            <div
              style={{
                padding: "10px",
                marginBottom: "15px",
                backgroundColor: "#e6fffa",
                color: "#065f46",
                borderRadius: "4px",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              {successMessage}
            </div>
          )}

          {infoMessage && (
            <div className="info-box" style={{ marginBottom: 16 }}>
              {infoMessage}
            </div>
          )}

          {mode === "email" && (
            <form onSubmit={handleSubmit}>
              <div className="signin-input-group">
                <input
                  type="text"
                  name="first_name"
                  placeholder="First Name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="signin-input"
                  required
                />
              </div>

              <div className="signin-input-group">
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last Name (optional)"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="signin-input"
                />
              </div>

              <div className="signin-input-group">
                <input
                  type="tel"
                  name="phone_number"
                  placeholder="Phone Number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  className="signin-input"
                  required
                />
              </div>

              <div className="signin-input-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  className="signin-input"
                  required
                />
              </div>

              <div className="signin-input-group">
                <input
                  type="password"
                  name="password"
                  placeholder="Password (min. 6 characters)"
                  value={formData.password}
                  onChange={handleChange}
                  className="signin-input"
                  required
                />
              </div>

              <div className="signin-input-group">
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="signin-input"
                  required
                />
              </div>

              {/* <div className="signin-input-group">
                <input
                  type="text"
                  name="referral_code"
                  placeholder="Referral Code (optional)"
                  value={formData.referral_code}
                  onChange={handleChange}
                  className="signin-input"
                />
              </div> */}

              <div className="signin-options" style={{ marginBottom: "20px" }}>
                <label className="remember-me">
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                  />
                  <span>I agree to Terms & Conditions</span>
                </label>
              </div>

              <button type="submit" className="signin-btn" disabled={isLoading}>
                {isLoading ? "CREATING ACCOUNT..." : "SIGN UP"}
              </button>
            </form>
          )}

          {mode === "phone" && (
            <div className="mobile-otp-section">
              {phoneStep === "phone" && (
                <>
                  <div className="signin-input-group">
                    <input
                      type="tel"
                      className="signin-input"
                      placeholder="Phone Number (+91XXXXXXXXXX)"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                  </div>

                  <button
                    type="button"
                    className="signin-btn"
                    onClick={handleSendOtp}
                    disabled={isLoading}
                  >
                    {isLoading ? "SENDING OTP..." : "NEXT"}
                  </button>
                </>
              )}

              {phoneStep === "otp" && (
                <>
                  <div className="signin-input-group">
                    <input
                      type="text"
                      className="signin-input"
                      placeholder="Enter OTP"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                    />
                  </div>

                  <button
                    type="button"
                    className="signin-btn"
                    onClick={handleVerifyOtp}
                    disabled={isLoading}
                  >
                    {isLoading ? "VERIFYING..." : "VERIFY OTP"}
                  </button>

                  <button
                    type="button"
                    className="mobile-btn"
                    onClick={handleSendOtp}
                    disabled={isLoading || resendCooldown > 0}
                  >
                    {resendCooldown > 0
                      ? `Resend OTP in 00:${String(resendCooldown).padStart(2, "0")}`
                      : "Resend OTP"}
                  </button>

                  <button
                    type="button"
                    className="mobile-btn"
                    onClick={resetPhoneState}
                    disabled={isLoading}
                  >
                    Change Number
                  </button>
                </>
              )}

              <div id="recaptcha-container-signup" />
            </div>
          )}

          <button
            type="button"
            className="mobile-btn"
            onClick={handleMobileModeClick}
            disabled={isLoading}
          >
            {mode === "email" ? "Sign Up With Mobile OTP" : "Use Email Signup Instead"}
          </button>

          {/* Social signup section hidden as requested */}
          {/* {mode === "email" && (
            <>
              <div className="signin-divider">
                <span>Or sign up with social networks</span>
              </div>

              <div className="social-buttons">
                <button className="social-btn google-btn" type="button">
                  <FaGoogle size={24} />
                </button>
                <button className="social-btn facebook-btn" type="button">
                  <FaFacebook size={24} />
                </button>
              </div>
            </>
          )} */}
        </div>

        {/* Sign In Link */}
        <div className="signup-link">
          <span>Already a Member? </span>
          <a href="#signin" onClick={(e) => {
            e.preventDefault();
            onSwitchToSignIn();
          }}>Sign In</a>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
